import { create } from 'zustand';
import type { AuthUser, MemberRole } from '@eriko/shared-types';

interface AuthOrg {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: AuthUser | null;
  organization: AuthOrg | null;
  role: MemberRole | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;

  setAuth: (data: {
    user: AuthUser;
    organization: AuthOrg;
    tokens: { accessToken: string; refreshToken: string };
    role?: MemberRole;
  }) => void;
  setRole: (role: MemberRole) => void;
  updateTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  hydrateFromStorage: () => boolean;
}

const TOKEN_KEY = 'eriko_access_token';
const REFRESH_KEY = 'eriko_refresh_token';
const USER_KEY = 'eriko_user';
const ORG_KEY = 'eriko_org';
const ROLE_KEY = 'eriko_role';

function persistToStorage(state: Partial<AuthState>) {
  if (state.accessToken) localStorage.setItem(TOKEN_KEY, state.accessToken);
  if (state.refreshToken) localStorage.setItem(REFRESH_KEY, state.refreshToken);
  if (state.user) localStorage.setItem(USER_KEY, JSON.stringify(state.user));
  if (state.organization) localStorage.setItem(ORG_KEY, JSON.stringify(state.organization));
  if (state.role) localStorage.setItem(ROLE_KEY, state.role);
}

function clearStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ORG_KEY);
  localStorage.removeItem(ROLE_KEY);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  organization: null,
  role: null,
  accessToken: null,
  refreshToken: null,
  isLoading: true,

  setAuth: ({ user, organization, tokens, role }) => {
    const state = {
      user,
      organization,
      role: role ?? null,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      isLoading: false,
    };
    persistToStorage(state);
    set(state);
  },

  setRole: (role) => {
    localStorage.setItem(ROLE_KEY, role);
    set({ role });
  },

  updateTokens: (tokens) => {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },

  clearAuth: () => {
    clearStorage();
    set({
      user: null,
      organization: null,
      role: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  hydrateFromStorage: () => {
    const accessToken = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const userRaw = localStorage.getItem(USER_KEY);
    const orgRaw = localStorage.getItem(ORG_KEY);
    const role = localStorage.getItem(ROLE_KEY) as MemberRole | null;

    if (!accessToken || !refreshToken || !userRaw || !orgRaw) {
      set({ isLoading: false });
      return false;
    }

    try {
      const user = JSON.parse(userRaw) as AuthUser;
      const organization = JSON.parse(orgRaw) as AuthOrg;
      set({ user, organization, role, accessToken, refreshToken, isLoading: false });
      return true;
    } catch {
      clearStorage();
      set({ isLoading: false });
      return false;
    }
  },
}));
